import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  authApi,
  subscriptionApi,
  ordersApi,
  paymentApi,
  adminApi,
  clearAuth,
  getToken,
  setToken,
  getCachedUser,
  setHasActivePlan,
  getDashboardPathForCurrentPage,
  type AuthUser,
  type Subscription,
  type Order,
  type AdminUser,
} from '../lib/api';
import { PRICING, PRICING_US_HOME, buildUsHomeVolumePlans, usHomeSubscriptionMonthlyUsd } from '../lib/pricing';
import {
  US_HOME_PRICING_SECTION,
  US_PARTNERS_PRICING_SECTION,
  US_DASHBOARD_SUBSCRIPTIONS_HEADING,
  US_PARTNER_VOLUME_PLANS,
} from '../lib/usPricingSectionCopy';
import NewOrderForm from './NewOrderForm';
import {
  Home,
  Plus,
  Clock,
  CreditCard,
  LogOut,
  Check,
  Plane,
  Package,
  ShoppingCart,
  HelpCircle,
  Camera,
  ArrowRight,
  Info,
  Users,
  Box,
  PlaneTakeoff
} from 'lucide-react';

const ORDER_DETAIL_LABELS: Record<string, { en: string; fr: string }> = {
  propertyName: { en: 'Property name', fr: 'Nom de la propriété' },
  matterportUrl: { en: 'Matterport link', fr: 'Lien Matterport' },
  isResidential: { en: 'Residential property?', fr: 'Bien résidentiel ?' },
  hasLogoAnimation: { en: 'Logo animation?', fr: 'Animation de logo ?' },
  agentLogoLink: { en: 'Logo link', fr: 'Lien du logo' },
  logoAnimationType: { en: 'Logo animation type', fr: "Type d'animation logo" },
  walkthroughIntroType: { en: 'Video introduction', fr: 'Introduction vidéo' },
  walkthroughIntroPhotoLink: { en: 'Exterior photo link', fr: 'Lien photo extérieure' },
  walkthroughFlyoverAddress: { en: 'Property address (flyover)', fr: 'Adresse (flyover)' },
  walkthroughFlyoverType: { en: 'Flyover style', fr: 'Style de vol' },
  droneClipsLink: { en: 'Drone clips link', fr: 'Lien clips drone' },
  hasTitle: { en: 'Title?', fr: 'Titre ?' },
  titleText: { en: 'Title text', fr: 'Texte du titre' },
  hasMusic: { en: 'Licensed music?', fr: 'Musique sous licence ?' },
  musicTrack: { en: 'Music track number', fr: 'Numéro de piste' },
  portfolioPermission: { en: 'Portfolio permission', fr: 'Autorisation portfolio' },
  notes: { en: 'Additional notes', fr: 'Notes complémentaires' },
  extraFeedback: { en: 'Your feedback', fr: 'Votre retour' },
  deliveryEmail: { en: 'Delivery email', fr: 'Email de livraison' },
  includeDrone: { en: 'Drone included', fr: 'Drone inclus' },
  showcaseApplyTemplate: { en: 'Showcase template?', fr: 'Modèle showcase ?' },
  showcaseLabel: { en: 'Showcase label', fr: 'Libellé showcase' },
  showcasePrice: { en: 'Price', fr: 'Prix' },
  showcaseAgentPhoto: { en: 'Agent photo', fr: 'Photo agent' },
  showcaseAgentName: { en: 'Agent name', fr: 'Nom agent' },
  showcaseAgentPhone: { en: 'Agent phone', fr: 'Téléphone agent' },
  showcaseAddress: { en: 'Address', fr: 'Adresse' },
  showcaseCity: { en: 'City', fr: 'Ville' },
  showcaseState: { en: 'State', fr: 'Région' },
  showcasePostalCode: { en: 'Postal code', fr: 'Code postal' },
  showcaseBedrooms: { en: 'Bedrooms', fr: 'Chambres' },
  showcaseBathrooms: { en: 'Bathrooms', fr: 'Salles de bain' },
  showcaseSqFt: { en: 'Square footage', fr: 'Surface' },
  showcaseHasMusic: { en: 'Showcase music?', fr: 'Musique showcase ?' },
  showcaseMusicTrack: { en: 'Showcase music track', fr: 'Piste showcase' },
  premiumPhotosLink: { en: 'Premium photos link', fr: 'Lien photos premium' },
  premiumPhotosNumbered: { en: 'Photos numbered?', fr: 'Photos numérotées ?' },
  premiumSlideshowTransition: { en: 'Slideshow transition', fr: 'Transition diaporama' },
  premiumHasMusic: { en: 'Premium music?', fr: 'Musique premium ?' },
  premiumMusicTrack: { en: 'Premium music track', fr: 'Piste premium' },
  shortApplyTemplate: { en: 'Short template?', fr: 'Modèle short ?' },
  shortPrice: { en: 'Price', fr: 'Prix' },
  shortAgentPhoto: { en: 'Agent photo', fr: 'Photo agent' },
  shortAgentName: { en: 'Agent name', fr: 'Nom agent' },
  shortAgentPhone: { en: 'Agent phone', fr: 'Téléphone agent' },
  shortAddress: { en: 'Address', fr: 'Adresse' },
  shortCity: { en: 'City', fr: 'Ville' },
  shortState: { en: 'State', fr: 'Région' },
  shortPostalCode: { en: 'Postal code', fr: 'Code postal' },
  shortBedrooms: { en: 'Bedrooms', fr: 'Chambres' },
  shortBathrooms: { en: 'Bathrooms', fr: 'Salles de bain' },
  shortSqFt: { en: 'Square footage', fr: 'Surface' },
  shortPhotosLink: { en: 'Short photos link', fr: 'Lien photos short' },
  shortPhotosNumbered: { en: 'Short photos numbered?', fr: 'Photos short numérotées ?' },
  shortSlideshowTransition: { en: 'Short transition', fr: 'Transition short' },
  shortHasMusic: { en: 'Short music?', fr: 'Musique short ?' },
  shortMusicTrack: { en: 'Short music track', fr: 'Piste short' },
};

function formatOrderDetailsForDisplay(details: Record<string, unknown>, isFR: boolean): { label: string; value: string }[] {
  const lang = isFR ? 'fr' : 'en';
  const out: { label: string; value: string }[] = [];
  for (const [key, value] of Object.entries(details)) {
    if (value === null || value === undefined) continue;
    if (value === '' || (typeof value === 'string' && (value as string).trim() === '')) continue;
    if (key === 'includeDrone' && value !== 'yes' && String(value).toLowerCase() !== 'true') continue;
    if (isFR && (key.startsWith('short') || key.startsWith('showcase') || key.startsWith('premium'))) continue;
    const labelMap = ORDER_DETAIL_LABELS[key];
    const label = labelMap ? labelMap[lang] : key;
    let displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (isFR && typeof value === 'string') {
      const v = (value as string).trim().toLowerCase();
      if (v === 'yes') displayValue = 'Oui';
      else if (v === 'no') displayValue = 'Non';
    }
    out.push({ label, value: displayValue });
  }
  return out;
}

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Source de vérité: URL réelle du navigateur pour que /fr et /fr/partenaires soient toujours en français
  const pathname = typeof window !== 'undefined' ? window.location.pathname : (location?.pathname || '');
  /** Comparaison insensible à la casse (/Partners/… vs /partners/…) pour tarifs + Stripe. */
  const pathLower = pathname.toLowerCase();
  const isFR = pathLower.startsWith('/fr');
  const isFRPartners = pathLower.startsWith('/fr/partenaires');
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : (location?.search || ''));
  const initialSectionFromUrl = searchParams.get('section');
  const homeHref = pathLower.startsWith('/fr/partenaires')
    ? '/fr/partenaires'
    : pathLower.startsWith('/partners')
      ? '/partners'
      : isFR
        ? '/fr'
        : '/';

  const [user, setUser] = useState<AuthUser | null>(null);
  const effectiveUserType = user?.user_type ?? getCachedUser()?.user_type;
  /** Sur /fr/partenaires/* : toujours grille photographes (95 €…), alignée avec Stripe partenaires. */
  const isFRPartnerPricing =
    isFR && (effectiveUserType === 'partner' || isFRPartners);
  const isFRAgentsPriceGrid = isFR && !isFRPartnerPricing;
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(initialSectionFromUrl || 'subscription');
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [resetPasswordNew, setResetPasswordNew] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState<'success' | 'error' | null>(null);

  const [viewWithDrone, setViewWithDrone] = useState(() => {
    const saved = localStorage.getItem('dashboard_viewWithDrone');
    return saved === 'true';
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAutoRedirectedNoSub, setHasAutoRedirectedNoSub] = useState(false);

  // Symbole monétaire selon le site : routes /fr/* → €, site US (/, /dashboard, /partners, …) → $
  const useEuros = isFR;
  /** Compte partenaire (US) : tarifs / grille partners même après retour sur l’accueil (logo). */
  const isPartnerUsAccount = effectiveUserType === 'partner' && !isFR;
  const isUsPartnersDashboard = !isFR && (pathLower.startsWith('/partners') || isPartnerUsAccount);
  /** US : home = 5/10/20 ; partners = 3/6/9 — les comptes partner suivent toujours la grille partners. */
  const isUsHomeDashboard = !isFR && !pathLower.startsWith('/partners') && !isPartnerUsAccount;

  const usHomeVolumePlans = useMemo(() => {
    if (!isUsHomeDashboard) return [];
    const base = viewWithDrone ? PRICING_US_HOME.extraCredit.withDrone : PRICING_US_HOME.extraCredit.withoutDrone;
    return buildUsHomeVolumePlans(base);
  }, [isUsHomeDashboard, viewWithDrone]);

  const usPartnerVolumePlans = useMemo(() => {
    if (!isUsPartnersDashboard || isFR) return [];
    return US_PARTNER_VOLUME_PLANS[viewWithDrone ? 'drone' : 'standard'];
  }, [isUsPartnersDashboard, isFR, viewWithDrone]);

  /** Tarifs unité (essai + crédit extra) type partenaires : /partners/dashboard (US) ou compte partenaire FR (/fr/partenaires/…). */
  const usePartnerUnitPricing =
    isUsPartnersDashboard || isFRPartnerPricing;

  /** Checkout Stripe US : `STRIPE_PRICES_US` (home) vs `STRIPE_PRICES_US_PARTNERS` (partners). */
  const stripeUsPriceSet: 'home' | 'partners' | undefined = isFR
    ? undefined
    : (isUsPartnersDashboard ? 'partners' : 'home');

  const trialPriceStandard = isFRAgentsPriceGrid
    ? 150
    : (usePartnerUnitPricing ? PRICING.trial.withoutDrone : PRICING_US_HOME.trial.withoutDrone);
  const trialPriceDrone = isFRAgentsPriceGrid
    ? 190
    : (usePartnerUnitPricing ? PRICING.trial.withDrone : PRICING_US_HOME.trial.withDrone);

  const extraCreditPriceStandard = isFRAgentsPriceGrid
    ? 150
    : (usePartnerUnitPricing ? PRICING.extraCredit.withoutDrone : PRICING_US_HOME.extraCredit.withoutDrone);
  const extraCreditPriceDrone = isFRAgentsPriceGrid
    ? 190
    : (usePartnerUnitPricing ? PRICING.extraCredit.withDrone : PRICING_US_HOME.extraCredit.withDrone);

  const toggleDroneView = (value: boolean) => {
    setViewWithDrone(value);
    localStorage.setItem('dashboard_viewWithDrone', value.toString());
  };

  const getPlanDetails = () => {
    if (!subscription) return { name: isFR ? 'Aucun forfait' : 'No Plan', properties: 0, price: 0 };

    if (subscription.plan === 'one-time') {
      const p = subscription.with_drone ? trialPriceDrone : trialPriceStandard;
      return { name: isFR ? 'Une propriété' : 'Single Property', properties: 1, price: p };
    }

    const plans: Record<string, { name: string; properties: number; price: number }> = {
      // Legacy partners
      starter: { name: isFR ? '3 vidéos/mois' : '3 videos/month', properties: 3, price: subscription.with_drone ? PRICING.starter.withDrone : PRICING.starter.withoutDrone },
      professional: { name: isFR ? '6 vidéos/mois' : '6 videos/month', properties: 6, price: subscription.with_drone ? PRICING.professional.withDrone : PRICING.professional.withoutDrone },
      enterprise: { name: isFR ? '9 vidéos/mois' : '9 videos/month', properties: 9, price: subscription.with_drone ? PRICING.enterprise.withDrone : PRICING.enterprise.withoutDrone },
      // New US home — même montants que la grille + Stripe (voir usHomeSubscriptionMonthlyUsd / STRIPE_PRICES_US)
      starter5: {
        name: '5 videos/month',
        properties: 5,
        price: usHomeSubscriptionMonthlyUsd('starter5', !!subscription.with_drone),
      },
      professional10: {
        name: '10 videos/month',
        properties: 10,
        price: usHomeSubscriptionMonthlyUsd('professional10', !!subscription.with_drone),
      },
      enterprise20: {
        name: '20 videos/month',
        properties: 20,
        price: usHomeSubscriptionMonthlyUsd('enterprise20', !!subscription.with_drone),
      },
    };
    return plans[subscription.plan] || { name: isFR ? 'Inconnu' : 'Unknown', properties: 0, price: 0 };
  };

  const handlePaymentRedirect = (url: string) => {
    window.location.href = url;
  };

  const handleBuyCredit = async (withDrone = false) => {
    setIsProcessing(true);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('pendingDroneExtraCredit', withDrone ? 'true' : 'false');
      }
      const { url } = await paymentApi.createCheckoutSession({
        region: isFR ? 'fr' : 'us',
        type: 'extra_credit',
        with_drone: withDrone,
        ...(!isFR && stripeUsPriceSet ? { stripe_us_price_set: stripeUsPriceSet } : {}),
      });
      handlePaymentRedirect(url);
    } catch (err) {
      alert((err instanceof Error ? err.message : (isFR ? 'Erreur de paiement' : 'Payment error')) || (isFR ? 'Impossible de lancer le paiement.' : 'Could not start checkout.'));
      setIsProcessing(false);
    }
  };

  const handleActivateSubscription = async (planId: string, withDrone = false) => {
    setIsProcessing(true);
    try {
      const { url } = await paymentApi.createCheckoutSession({
        region: isFR ? 'fr' : 'us',
        type: 'subscription',
        plan_id: planId,
        with_drone: withDrone,
        ...(!isFR && stripeUsPriceSet ? { stripe_us_price_set: stripeUsPriceSet } : {}),
      });
      handlePaymentRedirect(url);
    } catch (err) {
      alert((err instanceof Error ? err.message : (isFR ? 'Erreur d\'abonnement' : 'Subscription error')) || (isFR ? 'Impossible de lancer le paiement.' : 'Could not start checkout.'));
      setIsProcessing(false);
    }
  };

  const handleTryOneProperty = async (withDrone = false) => {
    setIsProcessing(true);
    try {
      const { url } = await paymentApi.createCheckoutSession({
        region: isFR ? 'fr' : 'us',
        type: 'trial',
        with_drone: withDrone,
        ...(!isFR && stripeUsPriceSet ? { stripe_us_price_set: stripeUsPriceSet } : {}),
      });
      handlePaymentRedirect(url);
    } catch (err) {
      alert((err instanceof Error ? err.message : (isFR ? 'Erreur essai' : 'Trial error')) || (isFR ? 'Impossible de lancer le paiement.' : 'Could not start checkout.'));
      setIsProcessing(false);
    }
  };

  const handleOpenStripePortal = async () => {
    setIsProcessing(true);
    try {
      const { url } = await paymentApi.createPortalSession();
      handlePaymentRedirect(url);
    } catch (err) {
      alert((err instanceof Error ? err.message : (isFR ? 'Erreur portail de facturation' : 'Billing portal error')) || (isFR ? 'Impossible d\'ouvrir le portail de facturation.' : 'Could not open billing portal.'));
      setIsProcessing(false);
    }
  };

  // TB partenaires : enregistrer user_type = partner en D1 si le compte était encore agent (connexion agences, etc.).
  useEffect(() => {
    if (loading || !user || user.user_type === 'partner') return;
    const pl = pathLower;
    const onPartnerDashboard =
      pl.startsWith('/fr/partenaires') ||
      pl === '/partners/dashboard' ||
      pl.startsWith('/partners/dashboard/');
    if (!onPartnerDashboard) return;
    let cancelled = false;
    (async () => {
      try {
        await authApi.claimPartnerRole();
        const u = await authApi.getUser();
        if (!cancelled && u) setUser(u);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, user?.user_id, user?.user_type, pathLower]);

  // Comptes partenaires : URL canonique /partners/dashboard (évite tarifs US home sur /dashboard).
  // Utilise le cache + user (API) : avant, seul user.user_type bloquait la redirection si user était encore null.
  useEffect(() => {
    const ut = user?.user_type ?? getCachedUser()?.user_type;
    if (ut !== 'partner') return;
    const pathNorm = pathLower.replace(/\/$/, '') || '/';
    if (!isFR && pathNorm === '/dashboard') {
      navigate('/partners/dashboard', { replace: true });
      return;
    }
    if (isFR && pathNorm === '/fr/dashboard') {
      navigate('/fr/partenaires/dashboard', { replace: true });
    }
  }, [user, pathLower, isFR, navigate]);

  // Handle Stripe return params (preserve /fr for French dashboard)
  // Si session_id est présent : on confirme côté API (crédit immédiat), refetch, puis passage au tableau de bord (sections dégrisées).
  // Sinon : polling pour récupérer l’abonnement une fois le webhook appliqué.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dashboardPath = getDashboardPathForCurrentPage();
    const sessionId = urlParams.get('session_id');
    const isSuccess =
      urlParams.get('payment') === 'success' ||
      urlParams.get('trial') === 'success' ||
      urlParams.get('subscription') === 'success';

    if (isSuccess) {
      localStorage.setItem('paymentJustCompleted', 'true');

      if (sessionId) {
        const region = pathLower.startsWith('/fr') ? 'fr' : 'us';
        paymentApi
          .confirmCheckoutSession(sessionId, region)
          .then(() => {
            window.history.replaceState({}, '', dashboardPath);
            return subscriptionApi.get().then((sub) => {
              setSubscription(sub);
              setHasActivePlan(true);
              setActiveSection('home');
            });
          })
          .then(() => {
            // Refetch après un court délai pour s’assurer que l’UI a bien l’abonnement actif (dégriser les sections).
            window.setTimeout(() => subscriptionApi.get().then(setSubscription).catch(() => {}), 800);
          })
          .catch((err) => {
            window.history.replaceState({}, '', dashboardPath);
            const msg = err?.message || (isFR ? 'Impossible d’activer l’abonnement. Réessayez ou contactez le support.' : 'Could not activate subscription. Please try again or contact support.');
            alert(msg);
          });
      } else {
        window.history.replaceState({}, '', dashboardPath);
        const refetchSubscription = () => subscriptionApi.get().then((sub) => { setSubscription(sub); if (sub?.status === 'active') setActiveSection('home'); }).catch(() => {});
        refetchSubscription();
        const t1 = window.setTimeout(refetchSubscription, 2000);
        const t2 = window.setTimeout(refetchSubscription, 5000);
        const t3 = window.setTimeout(refetchSubscription, 8000);
        return () => {
          window.clearTimeout(t1);
          window.clearTimeout(t2);
          window.clearTimeout(t3);
        };
      }
    }

    if (urlParams.get('trial') === 'cancelled') {
      window.history.replaceState({}, '', dashboardPath);
      setActiveSection('try-one');
      return;
    }

    if (urlParams.get('subscription') === 'cancelled') {
      window.history.replaceState({}, '', dashboardPath);
      setActiveSection('subscription');
      return;
    }

    if (urlParams.get('payment') === 'cancelled') {
      window.history.replaceState({}, '', dashboardPath);
      setActiveSection('buy-credit');
      return;
    }
  }, []);

  // Load admin users when opening the Inscrits section
  useEffect(() => {
    if (activeSection !== 'admin-users' || !user?.is_admin) return;
    setAdminUsersLoading(true);
    adminApi.listUsers()
      .then(setAdminUsers)
      .catch(() => setAdminUsers([]))
      .finally(() => setAdminUsersLoading(false));
  }, [activeSection, user?.is_admin]);

  // Load user + subscription + orders
  useEffect(() => {
    const loadData = async () => {
      const signinPath = pathLower.startsWith('/fr/partenaires')
        ? '/fr/partenaires/signin'
        : pathLower.startsWith('/fr')
          ? '/fr/signin'
          : pathLower.startsWith('/partners')
            ? '/partners/signin'
            : '/signin';

      const savedToken = getToken();
      if (!savedToken) {
        window.location.href = signinPath;
        return;
      }

      const delays = [0, 500, 1200];
      let currentUser = null;
      for (const delay of delays) {
        if (delay > 0) {
          if (!getToken() && savedToken) setToken(savedToken);
          await new Promise((r) => setTimeout(r, delay));
        }
        currentUser = await authApi.getUser();
        if (currentUser) break;
      }
      if (!currentUser) {
        clearAuth();
        window.location.href = signinPath;
        return;
      }
      setUser(currentUser);

      const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      const isStripeReturnWithSession =
        (urlParams.get('subscription') === 'success' || urlParams.get('payment') === 'success' || urlParams.get('trial') === 'success') &&
        !!urlParams.get('session_id');

      try {
        const sub = await subscriptionApi.get();
        // Ne pas écraser avec l’ancien état si on revient de Stripe avec session_id : l’effet "Stripe return" fera confirm + refetch.
        if (!isStripeReturnWithSession) setSubscription(sub);

        const hasCredits = (sub?.credits_remaining ?? 0) > 0;
        const hasActivePlan = sub?.status === 'active' && !!sub.plan && sub.plan !== 'free' && sub.plan !== 'none';
        setHasActivePlan(hasCredits || hasActivePlan);

        if (!hasCredits && !hasActivePlan && !isStripeReturnWithSession) {
          let onboardingPath: string;
          if (pathLower.startsWith('/fr/partenaires')) {
            onboardingPath = '/fr/partenaires/onboarding/plan';
          } else if (pathLower.startsWith('/fr')) {
            onboardingPath = '/fr/onboarding/plan';
          } else if (pathLower.startsWith('/partners')) {
            onboardingPath = '/partners/onboarding/plan';
          } else {
            onboardingPath = '/onboarding/plan';
          }
          window.location.replace(onboardingPath);
          return;
        }
      } catch {
        if (!isStripeReturnWithSession) setSubscription(null);
      }

      try {
        const orderList = await ordersApi.list();
        setOrders(orderList);
      } catch {
        setOrders([]);
      }

      setLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    if (loading || hasAutoRedirectedNoSub) return;
    const hasRealSubscription = subscription && subscription.status === 'active' && subscription.plan !== 'one-time';
    if (!hasRealSubscription && (activeSection === 'home' || activeSection === 'subscription')) {
      setActiveSection('try-one');
      setHasAutoRedirectedNoSub(true);
    }
  }, [loading, activeSection, subscription, hasAutoRedirectedNoSub]);

  // Post-load redirects
  useEffect(() => {
    if (!loading && subscription) {
      const paymentJustCompleted = localStorage.getItem('paymentJustCompleted');
      if (paymentJustCompleted === 'true') {
        localStorage.removeItem('paymentJustCompleted');
        setActiveSection('home');
        return;
      }

      const justSubmittedOrder = localStorage.getItem('orderJustSubmitted');
      if (justSubmittedOrder === 'true') {
        localStorage.removeItem('orderJustSubmitted');
        setActiveSection('home');
        setTimeout(() => alert(isFR ? 'Commande enregistrée avec succès ! 🚀' : 'Order submitted successfully! 🚀'), 500);
        return;
      }

      if (subscription.status === 'active' && activeSection === 'subscription') {
        setActiveSection('home');
      }
    }
  }, [loading, subscription]);

  const handleSignOut = async () => {
    try {
      await authApi.logout();
    } catch {
      clearAuth();
    }
    const p = (typeof window !== 'undefined' ? window.location.pathname : '').toLowerCase();
    const homeAfterSignOut = p.startsWith('/fr/partenaires')
      ? '/fr/partenaires'
      : p.startsWith('/fr')
        ? '/fr'
        : p.startsWith('/partners')
          ? '/partners'
          : '/';
    window.location.href = homeAfterSignOut;
  };

  const handleNavigateToNewOrder = () => {
    if (!subscription) return;

    if (Number(subscription.credits_remaining) > 0) {
      setActiveSection('new-order');
    } else {
      // Si l'utilisateur a déjà un abonnement actif (même one-time),
      // on évite de l'envoyer vers "try-one" (essai) et on le dirige vers:
      // - "subscription" pour passer en mensuel
      // - ou "buy-credit" si c'est un mensuel sans crédits
      const hasActive = subscription.status === 'active';
      const isOneTime = subscription.plan === 'one-time';
      if (hasActive && isOneTime) {
        setActiveSection('subscription');
      } else {
        const isSubscriber = subscription.plan !== 'one-time' && hasActive;
        if (isSubscriber) setActiveSection('buy-credit');
        else setActiveSection(canTryOne ? 'try-one' : 'subscription');
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleNewOrderSubmit = async (formData: {
    propertyName: string;
    matterportUrl: string;
    deliveryEmail: string;
    isResidential: string;
    [key: string]: unknown;
  }) => {
    if (!formData.propertyName || !formData.matterportUrl) {
      alert(isFR ? 'Veuillez remplir tous les champs obligatoires.' : 'Please fill in all required fields');
      return;
    }
    if (formData.isResidential === 'no') {
      alert(isFR ? 'Service réservé aux propriétés résidentielles.' : 'Service only for residential properties.');
      return;
    }
    if (!formData.deliveryEmail) {
      alert(isFR ? 'Veuillez confirmer l\'email de livraison.' : 'Please confirm delivery email.');
      return;
    }

    if (!subscription || Number(subscription.credits_remaining) <= 0) {
      handleNavigateToNewOrder();
      return;
    }

    setIsProcessing(true);
    try {
      const hasDroneInPlan = subscription.with_drone === true;
      const hasDroneClips =
        String((formData as any).droneClipsLink || '')
          .trim()
          .length > 0;

      const normalizedOrderDetails = {
        ...formData,
        // Always send a boolean includeDrone flag:
        // - true if the subscription includes drone, or if clips are provided (extra credit)
        // - false when the user has no drone option and no clips
        includeDrone: hasDroneInPlan || hasDroneClips,
      };

      const result = await ordersApi.create({
        property_name: formData.propertyName,
        matterport_url: formData.matterportUrl,
        order_details: normalizedOrderDetails,
        locale: isFR ? 'fr' : 'en',
      });

      if (!result.success) throw new Error('Order submission failed');

      // Refresh subscription (credits were deducted)
      const updatedSub = await subscriptionApi.get();
      setSubscription(updatedSub);

      if (typeof window !== 'undefined' && String(formData.droneClipsLink || '').trim() !== '') {
        localStorage.removeItem('pendingDroneExtraCredit');
      }
      localStorage.setItem('orderJustSubmitted', 'true');
      window.location.reload();
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      const noCredits = /credit|insufficient/i.test(msg);
      if (noCredits) {
        alert(isFR ? 'Crédits insuffisants ou expirés. Utilisez vos crédits dans les 6 mois suivant leur attribution.' : 'No credits remaining or credits have expired. Credits are valid for 6 months from when they were added.');
      } else {
        alert(isFR ? 'Échec de l\'envoi de la commande. Veuillez réessayer.' : 'Failed to submit order. Please try again.');
      }
      setIsProcessing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-b from-white to-[#3C59B2]/10 flex items-center justify-center text-gray-600">{isFR ? 'Chargement...' : 'Loading...'}</div>;
  }

  const hasActiveSubscription = subscription && subscription.status === 'active';
  const isSubscriber = hasActiveSubscription && subscription.plan !== 'one-time';
  const isOneTimeUser = subscription?.plan === 'one-time';
  // Try One est disponible uniquement si:
  // - pas d'abonnement actif
  // - et l'essai n'a pas déjà été utilisé
  const canTryOne = !!subscription && !hasActiveSubscription && !subscription.has_used_trial;
  const creditsRemaining = Number(subscription?.credits_remaining ?? 0);
  const extraCreditsPurchasedThisMonth = Number(subscription?.extra_credits_purchased_this_month ?? 0);
  const canOpenNewOrder = !!hasActiveSubscription && creditsRemaining > 0;
  const planDetails = getPlanDetails();

  const navItems = [
    { id: 'home', icon: Home, label: isFR ? 'Tableau de bord' : 'Dashboard', disabled: !hasActiveSubscription, reason: isFR ? 'Choisissez un plan pour accéder au tableau de bord.' : 'Please choose a plan to access the dashboard.' },
    {
      id: 'new-order',
      icon: Plus,
      label: isFR ? 'Nouvelle commande' : 'New Order',
      disabled: !canOpenNewOrder,
      reason: isFR
        ? creditsRemaining > 0
          ? 'Activez d\'abord un plan.'
          : 'Plus de crédits. Achetez un crédit ou reprenez plus tard.'
        : creditsRemaining > 0
          ? 'Please activate a plan first.'
          : 'No credits remaining. Buy credits or try later.',
    },
    { id: 'orders', icon: Clock, label: isFR ? 'Commandes passées' : 'Past Orders', disabled: !hasActiveSubscription, reason: isFR ? 'Aucune commande pour l\'instant.' : 'No past orders yet.' },
    { id: 'subscription', icon: CreditCard, label: isFR ? 'Abonnements' : 'Subscriptions', disabled: false, reason: '' },
    { id: 'buy-credit', icon: ShoppingCart, label: isFR ? 'Acheter un crédit' : 'Buy Extra Credit', disabled: !isSubscriber || creditsRemaining > 0, reason: isSubscriber ? (isFR ? 'Il vous reste des crédits. Utilisez-les d\'abord.' : 'You still have credits remaining. Use them first.') : (isFR ? 'Réservé aux abonnés.' : 'Exclusive to subscription members.') },
    ...(canTryOne ? [{ id: 'try-one', icon: Package, label: isFR ? 'Essayer une propriété' : 'Try One Property', disabled: false, reason: '' }] : []),
    ...(user?.is_admin ? [{ id: 'admin-users', icon: Users, label: 'Inscrits', disabled: false, reason: '' as const }] : []),
  ];

  const FeatureRow = ({ text, highlight = false, isExtra = false, useInfoIcon = false }: { text: string; highlight?: boolean; isExtra?: boolean; useInfoIcon?: boolean }) => {
    return (
      <div className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
        <div className="flex items-center min-w-0 flex-1">
          {useInfoIcon ? (
            <span className="mr-2 shrink-0"><Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" /></span>
          ) : (
            <div className={`p-1 rounded-full mr-2 shrink-0 ${isExtra ? 'bg-[#3C59B2]/10' : highlight ? 'bg-[#3C59B2]/10' : 'bg-green-50'}`}>
              {isExtra ? <Plus className="w-3 h-3 text-[#3C59B2]" /> : highlight ? <Plane className="w-3 h-3 text-[#3C59B2]" /> : <Check className="w-3 h-3 text-green-500" />}
            </div>
          )}
          <span className={`text-sm whitespace-pre-line ${isExtra ? 'font-medium text-[#3C59B2]' : highlight ? 'font-medium text-[#3C59B2]' : 'text-gray-600'}`}>{text}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#3C59B2]/10 flex flex-col">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-md px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 hover:bg-gray-100 rounded-lg">
          <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <a href={homeHref} className="flex-1 flex justify-center"><img src="/logo3D.png" alt="3DtoVideo" className="h-7" /></a>
        <div className="w-10" />
      </div>
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />}

      <div className="flex flex-1 relative">
        {/* SIDEBAR */}
        <div className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-lg flex flex-col transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <div className="p-6 border-b hidden lg:flex justify-center items-center">
            <a href={homeHref}><img src="/logo3D.png" alt="3DtoVideo" className="h-8 w-auto" /></a>
          </div>
          <nav className="font-manrope p-4 space-y-2 flex-1 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.disabled) {
                    if (item.id === 'buy-credit' || item.id === 'try-one') alert(item.reason);
                  } else {
                    if (item.id === 'new-order') handleNavigateToNewOrder();
                    else setActiveSection(item.id);
                    setIsMobileMenuOpen(false);
                  }
                }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-full transition-colors group relative
                  ${item.disabled ? 'text-gray-400 cursor-not-allowed opacity-60' : activeSection === item.id ? 'bg-[#3C59B2] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </div>
                {item.disabled && (item.id === 'buy-credit' || item.id === 'try-one') && (
                  <div className="relative group/icon">
                    <HelpCircle className="w-5 h-5 text-black stroke-[2.5] hover:text-gray-700" />
                    <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 w-48 bg-gray-900 text-white text-xs p-2 rounded shadow-lg opacity-0 group-hover/icon:opacity-100 pointer-events-none transition-opacity z-50 text-center">
                      {item.reason}
                      <div className="absolute top-1/2 left-full -translate-y-1/2 border-4 border-transparent border-l-gray-900"></div>
                    </div>
                  </div>
                )}
                {item.id === 'new-order' && creditsRemaining === 0 && !item.disabled && (
                  <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full font-bold">0</span>
                )}
                {item.id === 'buy-credit' && isSubscriber && creditsRemaining === 0 && (
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">Unlock</span>
                )}
              </button>
            ))}
            <a href="mailto:contact@3dtovideo.com" className="w-full flex items-center space-x-3 px-4 py-3 rounded-full text-gray-600 hover:bg-gray-100">
              <HelpCircle className="w-5 h-5" /><span className="font-medium">{isFR ? 'Support' : 'Support'}</span>
            </a>
          </nav>
          <div className="p-4 border-t bg-white mt-auto">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-[#3C59B2] rounded-full flex items-center justify-center text-white font-bold">
                {user?.email?.[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name || (isFR ? 'Utilisateur' : 'User')}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button onClick={handleSignOut} className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-full">
              <LogOut className="w-4 h-4" /><span className="text-sm font-medium">{isFR ? 'Déconnexion' : 'Sign Out'}</span>
            </button>
          </div>
        </div>

        <div className="font-manrope flex-1 p-6 lg:p-8 overflow-y-auto">
          {/* Temporary closure banner: Limited availability in April */}
          <div className="mb-6">
            <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row gap-3 items-start">
              <div className="flex-shrink-0 rounded-full bg-amber-100 text-amber-700 w-8 h-8 flex items-center justify-center font-bold">
                !
              </div>
              <div className="text-sm sm:text-base text-amber-900">
                {isFR ? (
                  <div className="space-y-1">
                    <p className="font-semibold">
                      ⚠️ Info Livraison Vidéo
                    </p>
                    <p>
                      Nous sommes fermés les <strong>23 et 24 avril</strong> pour maintenance planifiée. Les vidéos commandées la veille ou durant ces dates seront <strong>traitées et livrées dès notre retour</strong>. Le délai habituel de <strong>24h</strong> s&apos;appliquera à nouveau après ces dates.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="font-semibold">
                      ⚠️ Video Delivery Update
                    </p>
                    <p>
                      We are closed on <strong>April 23 &amp; 24</strong> for scheduled maintenance. Videos ordered the day before or during these dates will be <strong>processed and delivered upon our return</strong>. Our standard <strong>24h</strong> turnaround time will resume after these dates.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* DASHBOARD HOME */}
          {activeSection === 'home' && hasActiveSubscription && (
            <div>
              <h1 className="font-manrope text-2xl sm:text-3xl font-bold text-gray-900 mb-6 animate-fade-in-up">{isFR ? 'Bon retour ! 👋' : 'Welcome back! 👋'}</h1>
              {creditsRemaining === 0 && (
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-8 rounded-r-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-orange-800">{isFR ? 'Il ne vous reste plus de crédits.' : 'You have 0 credits remaining.'}</p>
                      <p className="text-sm text-orange-700 mt-0.5">{isFR ? 'Les crédits sont valables 6 mois à partir de leur attribution.' : 'Credits are valid for 6 months from when they were added.'}</p>
                      <p className="text-sm text-orange-700">{isOneTimeUser ? (isFR ? 'Abonnez-vous à un plan mensuel pour continuer.' : 'Subscribe to a monthly plan to continue ordering.') : (isFR ? 'Achetez un crédit pour commander tout de suite.' : 'Buy an extra credit to order immediately.')}</p>
                    </div>
                    <button onClick={() => setActiveSection(isOneTimeUser ? 'subscription' : 'buy-credit')} className="btn-premium overflow-hidden bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-orange-700 transition-colors">
                      {isOneTimeUser ? (isFR ? 'Voir les offres' : 'View Plans') : (isFR ? 'Acheter un crédit' : 'Buy Credit')}
                    </button>
                  </div>
                </div>
              )}
              <div className="card-hover bg-white rounded-3xl p-6 border border-[#E2E8F0] mb-8">
                <h2 className="font-manrope text-xl font-bold text-gray-900 mb-4">{isFR ? 'Votre offre' : 'Current Plan'}</h2>
                <div className="mb-6">
                  <p className="text-2xl font-bold text-[#3C59B2]">{planDetails.name}</p>
                  {subscription.plan === 'one-time' ? (
                    <p className="text-gray-600">{isFR ? 'Achat unique' : 'One-time purchase'}</p>
                  ) : (
                    <p className="text-gray-600 mt-2">{planDetails.properties} {isFR ? 'crédits/mois' : 'credits/month'}</p>
                  )}
                  {subscription.with_drone && (
                    <div className="flex items-center space-x-2 mt-2">
                      <Plane className="w-4 h-4 text-[#3C59B2]" />
                      <span className="text-sm text-gray-600">{isFR ? 'Drone inclus' : 'Drone included'}</span>
                    </div>
                  )}
                </div>
                <div className="pt-6 border-t">
                  <h3 className="text-gray-600 text-sm font-semibold">
                    {isFR ? 'Crédits restants' : 'Credits Remaining'}
                  </h3>
                  <p className="text-4xl font-bold text-[#3C59B2]">
                    {creditsRemaining}
                  </p>
                  {creditsRemaining > 0 && subscription.credits_expire_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      {isFR ? 'Valable jusqu\'au ' : 'Valid until '}
                      {new Date(subscription.credits_expire_at).toLocaleDateString(isFR ? 'fr-FR' : undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                  {isSubscriber && extraCreditsPurchasedThisMonth > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="text-gray-600 text-sm font-semibold">
                        {isFR ? 'Crédits additionnels achetés ce mois' : 'Extra credits purchased this month'}
                      </h3>
                      <p className="text-4xl font-bold text-[#3C59B2]">
                        {extraCreditsPurchasedThisMonth}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-[#3C59B2] rounded-3xl p-8 text-white">
                <h2 className="font-manrope text-2xl font-bold mb-2">{isFR ? 'Prêt pour votre prochaine propriété ?' : 'Ready for your next property?'}</h2>
                <p className="text-blue-100 mb-6">{isFR ? 'Passez une nouvelle commande.' : 'Submit a new order now.'}</p>
                <button
                  onClick={handleNavigateToNewOrder}
                  disabled={!canOpenNewOrder}
                  className={`btn-premium overflow-hidden text-white px-6 py-3 rounded-full font-semibold transition-colors ${
                    canOpenNewOrder ? 'bg-[#3C59B2] hover:bg-[#2d4a8f]' : 'bg-[#3C59B2]/40 opacity-70 cursor-not-allowed'
                  }`}
                >
                  {isFR ? 'Nouvelle commande' : 'Create New Order'}
                </button>
              </div>
            </div>
          )}

          {/* NEW ORDER */}
          {activeSection === 'new-order' && hasActiveSubscription && (
            <div>
              <h1 className="font-manrope text-2xl font-bold text-gray-900 mb-6 animate-fade-in-up">{isFR ? 'Nouvelle commande' : 'Create New Order'}</h1>
              {creditsRemaining > 0 ? (
                <NewOrderForm subscription={subscription} userEmail={user?.email} isFR={isFR} onSubmit={handleNewOrderSubmit} onCancel={() => setActiveSection('home')} />
              ) : (
                <div className="card-hover text-center py-12 bg-white rounded-3xl border border-[#E2E8F0]">
                  <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShoppingCart className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-manrope text-xl font-bold text-gray-800 mb-2">{isFR ? 'Il vous faut des crédits pour commander' : 'You need credits to order'}</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    {isOneTimeUser ? (isFR ? 'Vous avez utilisé votre crédit d\'essai. Passez à un plan mensuel pour continuer.' : "You've used your trial credit. Upgrade to a monthly plan to continue.") : (isFR ? 'Vous avez utilisé tous vos crédits mensuels. Achetez un crédit pour continuer.' : "You've used all your monthly credits. Buy an extra credit to continue immediately.")}
                  </p>
                  <button onClick={() => setActiveSection(isOneTimeUser ? 'subscription' : 'buy-credit')} className="btn-premium overflow-hidden bg-[#3C59B2] text-white px-8 py-3 rounded-full font-bold hover:bg-[#2d4a8f] transition-colors">
                    {isOneTimeUser ? (isFR ? 'Voir les abonnements' : 'View Subscription Plans') : (isFR ? 'Acheter un crédit' : 'Buy Extra Credit')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* PAST ORDERS */}
          {activeSection === 'orders' && hasActiveSubscription && (
            <div>
              <h1 className="font-manrope text-2xl font-bold text-gray-900 mb-6 animate-fade-in-up">{isFR ? 'Commandes passées' : 'Past Orders'}</h1>
              {orders.length === 0 ? (
                <div className="text-center p-12 bg-white rounded-3xl border border-[#E2E8F0] text-gray-500">{isFR ? 'Aucune commande.' : 'No orders yet.'}</div>
              ) : (
                <div className="space-y-4">
                  {orders
                    .slice()
                    .sort((a, b) => (a.property_name || '').localeCompare(b.property_name || ''))
                    .map((order) => {
                      const details = (order.order_details || {}) as Record<string, unknown>;
                      const hasDetails = details && Object.keys(details).length > 0;
                      return (
                        <details
                          key={order.order_id}
                          className="card-hover bg-white p-6 rounded-3xl border border-[#E2E8F0]"
                        >
                          <summary className="flex items-center justify-between cursor-pointer list-none">
                            <div>
                              <h3 className="font-manrope font-bold text-lg">
                                {order.property_name || (isFR ? 'Propriété sans nom' : 'Unnamed property')}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {isFR ? 'Commandé le ' : 'Ordered on '}
                                {new Date(order.created_at).toLocaleDateString(isFR ? 'fr-FR' : undefined)}
                              </p>
                            </div>
                            <span className="text-xs font-semibold text-[#3C59B2] uppercase tracking-wide">
                              {isFR ? 'Voir les détails' : 'View details'}
                            </span>
                          </summary>
                          <div className="mt-4">
                            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm md:text-base text-slate-800 space-y-1.5">
                              <p className="leading-snug">
                                <strong className="font-semibold text-slate-900">{isFR ? 'Référence' : 'Order ID'} :</strong>{' '}
                                <span className="font-normal">{order.order_id}</span>
                              </p>
                              <p className="leading-snug">
                                <strong className="font-semibold text-slate-900">{isFR ? 'Propriété' : 'Property'} :</strong>{' '}
                                <span className="font-normal">{order.property_name || '—'}</span>
                              </p>
                              <p className="leading-snug">
                                <strong className="font-semibold text-slate-900">{isFR ? 'Lien Matterport' : 'Matterport link'} :</strong>{' '}
                                {order.matterport_url ? (
                                  <a href={order.matterport_url} target="_blank" rel="noreferrer" className="text-[#3C59B2] hover:underline font-normal">{order.matterport_url}</a>
                                ) : (
                                  <span className="font-normal">—</span>
                                )}
                              </p>
                              {hasDetails && formatOrderDetailsForDisplay(details, isFR).map(({ label, value }, i) => (
                                <p key={`${label}-${i}`} className="leading-snug">
                                  <strong className="font-semibold text-slate-900">{label} :</strong>{' '}
                                  <span className="font-normal">{value}</span>
                                </p>
                              ))}
                            </div>
                          </div>
                        </details>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* ADMIN: Liste des inscrits */}
          {activeSection === 'admin-users' && user?.is_admin && (
            <div>
              <h1 className="font-manrope text-2xl font-bold text-gray-900 mb-6 animate-fade-in-up">Inscrits</h1>

              <div className="card-hover bg-white rounded-3xl border border-[#E2E8F0] p-6 mb-8">
                <h2 className="font-manrope font-bold text-gray-900 mb-4">Réinitialiser un mot de passe</h2>
                <p className="text-sm text-gray-600 mb-4">Indiquez l’email du compte et le nouveau mot de passe (min. 6 caractères).</p>
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                    <input
                      type="email"
                      value={resetPasswordEmail}
                      onChange={(e) => { setResetPasswordEmail(e.target.value); setResetPasswordMessage(null); }}
                      placeholder="utilisateur@exemple.com"
                      className="border border-gray-300 rounded-lg px-4 py-2 w-64"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Nouveau mot de passe</label>
                    <input
                      type="password"
                      value={resetPasswordNew}
                      onChange={(e) => { setResetPasswordNew(e.target.value); setResetPasswordMessage(null); }}
                      placeholder="••••••••"
                      className="border border-gray-300 rounded-lg px-4 py-2 w-48"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={!resetPasswordEmail.trim() || resetPasswordNew.length < 6 || resetPasswordLoading}
                    onClick={async () => {
                      setResetPasswordMessage(null);
                      setResetPasswordLoading(true);
                      try {
                        await adminApi.resetPassword(resetPasswordEmail.trim(), resetPasswordNew);
                        setResetPasswordMessage('success');
                        setResetPasswordEmail('');
                        setResetPasswordNew('');
                      } catch {
                        setResetPasswordMessage('error');
                      } finally {
                        setResetPasswordLoading(false);
                      }
                    }}
                    className="btn-premium overflow-hidden px-6 py-2 rounded-full font-semibold bg-[#3C59B2] text-white hover:bg-[#2d4a8f] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resetPasswordLoading ? 'Envoi...' : 'Réinitialiser'}
                  </button>
                </div>
                {resetPasswordMessage === 'success' && <p className="mt-3 text-sm text-green-600">Mot de passe mis à jour.</p>}
                {resetPasswordMessage === 'error' && <p className="mt-3 text-sm text-red-600">Erreur (email inconnu ou problème serveur).</p>}
              </div>

              {adminUsersLoading ? (
                <div className="text-center p-12 bg-white rounded-3xl border border-[#E2E8F0] text-gray-500">Chargement...</div>
              ) : adminUsers.length === 0 ? (
                <div className="text-center p-12 bg-white rounded-3xl border border-[#E2E8F0] text-gray-500">Aucun utilisateur.</div>
              ) : (
                <div className="card-hover bg-white rounded-3xl border border-[#E2E8F0] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="font-manrope font-semibold text-gray-700 px-6 py-4">Nom</th>
                          <th className="font-manrope font-semibold text-gray-700 px-6 py-4">Email</th>
                          <th className="font-manrope font-semibold text-gray-700 px-6 py-4">Entreprise</th>
                          <th className="font-manrope font-semibold text-gray-700 px-6 py-4">Inscription</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminUsers.map((u) => (
                          <tr key={u.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">{u.name}</td>
                            <td className="px-6 py-4 text-gray-600">{u.email}</td>
                            <td className="px-6 py-4 text-gray-600">{u.company_name || '—'}</td>
                            <td className="px-6 py-4 text-gray-500 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="px-6 py-3 text-sm text-gray-500 border-t border-gray-100">{adminUsers.length} utilisateur(s)</p>
                </div>
              )}
            </div>
          )}

          {/* SUBSCRIPTIONS */}
          {activeSection === 'subscription' && (
            <div>
              {hasActiveSubscription && !isOneTimeUser ? (
                <div className="card-hover bg-white rounded-3xl p-6 border border-[#E2E8F0] mb-8">
                  <h2 className="font-manrope text-xl font-bold mb-4">{isFR ? 'Votre abonnement' : 'Your Subscription'}</h2>
                  <p className="text-2xl text-[#3C59B2] font-bold">{planDetails.name}</p>
                  <p>{planDetails.properties} {isFR ? 'vidéos/mois' : 'properties/month'} • {useEuros ? '€' : '$'}{planDetails.price}/{isFR ? 'mois' : 'mo'}</p>
                  <button onClick={handleOpenStripePortal} className="btn-premium overflow-hidden mt-4 px-6 py-2 bg-[#3C59B2] text-white rounded-full hover:bg-[#2d4a8f] transition-colors">
                    {isFR ? 'Gérer la facturation' : 'Manage Billing'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-16">
                    <h2 className="font-manrope text-2xl md:text-3xl font-bold text-gray-900 mb-6 animate-fade-in-up">
                      {isUsHomeDashboard || (isUsPartnersDashboard && !isFR)
                        ? US_DASHBOARD_SUBSCRIPTIONS_HEADING
                        : isFR
                          ? 'Choisissez votre abonnement'
                          : 'Choose Your Subscription'}
                    </h2>

                    {isOneTimeUser && (
                      <div className="bg-green-50 border border-green-200 p-3 rounded-xl mb-6 text-center max-w-xl mx-auto">
                        <p className="text-green-700 text-sm"><span className="font-bold">{isFR ? 'Prêt à passer à l\'échelle ?' : 'Ready to scale?'}</span> {isFR ? 'Passez à un plan pour plus de crédits au meilleur prix.' : 'Upgrade to get more credits at the best price.'}</p>
                      </div>
                    )}

                    <div className="flex flex-row gap-3 justify-center items-center">
                      {isUsHomeDashboard ? (
                        <>
                          <button type="button" onClick={() => toggleDroneView(false)} className={`btn-premium overflow-hidden whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${!viewWithDrone ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
                            <span className="flex items-center gap-2"><Box className="w-4 h-4" />{US_HOME_PRICING_SECTION.standardTab}</span>
                          </button>
                          <button type="button" onClick={() => toggleDroneView(true)} className={`btn-premium overflow-hidden whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${viewWithDrone ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
                            <span className="flex items-center gap-2"><PlaneTakeoff className="w-4 h-4" />{US_HOME_PRICING_SECTION.droneTab}</span>
                          </button>
                        </>
                      ) : isUsPartnersDashboard && !isFR ? (
                        <>
                          <button type="button" onClick={() => toggleDroneView(false)} className={`btn-premium overflow-hidden whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${!viewWithDrone ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
                            <span className="flex items-center gap-2"><Box className="w-4 h-4" />{US_PARTNERS_PRICING_SECTION.standardTab}</span>
                          </button>
                          <button type="button" onClick={() => toggleDroneView(true)} className={`btn-premium overflow-hidden whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${viewWithDrone ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
                            <span className="flex items-center gap-2"><PlaneTakeoff className="w-4 h-4" />{US_PARTNERS_PRICING_SECTION.droneTab}</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={() => toggleDroneView(false)} className={`btn-premium overflow-hidden whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${!viewWithDrone ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
                            <span className="flex items-center gap-2"><Camera className="w-4 h-4" />{isFR ? 'Standard' : 'Standard'}</span>
                          </button>
                          <button type="button" onClick={() => toggleDroneView(true)} className={`btn-premium overflow-hidden whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${viewWithDrone ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
                            <span className="flex items-center gap-2"><Plane className="w-4 h-4" />+ {isFR ? 'Drone' : 'drone clips'}</span>
                          </button>
                        </>
                      )}
                    </div>

                    <div className="mt-6 flex justify-center">
                      <div className="inline-flex items-center gap-3 bg-white border border-[#E2E8F0] px-5 py-2.5 rounded-full shadow-sm">
                        <div className="w-5 h-5 rounded-full bg-[#3C59B2] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">i</span>
                        </div>
                        <span className="text-sm text-gray-600">
                          {isUsHomeDashboard
                            ? US_HOME_PRICING_SECTION.creditsInfo
                            : isUsPartnersDashboard && !isFR
                              ? US_PARTNERS_PRICING_SECTION.creditsInfo
                              : isFR
                                ? '1 crédit = 1 vidéo · Crédits valables 6 mois'
                                : '1 credit = 1 video · Credits valid for 6 months'}
                        </span>
                      </div>
                    </div>

                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {isUsHomeDashboard ? (
                      <>
                        {usHomeVolumePlans.map((plan, idx) => {
                          const planIds = ['starter5', 'professional10', 'enterprise20'] as const;
                          const planId = planIds[idx];
                          const isPopular = !!plan.popular;
                          return (
                            <div
                              key={plan.videos}
                              className={`card-hover bg-white rounded-3xl p-6 border relative flex flex-col ${
                                isPopular ? 'border-2 border-[#3C59B2] z-10 md:-translate-y-4' : 'border border-[#E2E8F0]'
                              }`}
                            >
                              {plan.savingsLabel ? (
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-700 px-6 py-1.5 rounded-full text-xs font-bold shadow-md whitespace-nowrap">
                                  {plan.savingsLabel}
                                </div>
                              ) : null}
                              <div className="text-center mb-6">
                                <h3 className="font-manrope text-xl font-bold text-gray-900">{plan.videos} videos/month</h3>
                                <div className={`mt-3 text-3xl sm:text-4xl font-bold tabular-nums ${isPopular ? 'text-[#3C59B2]' : 'text-slate-900'}`}>
                                  ${plan.monthlyPrice}
                                  <span className="text-lg sm:text-xl font-semibold text-slate-500">/mo</span>
                                </div>
                                <div className="text-sm text-slate-500 mt-2">
                                  ${plan.unitPrice} <span className="text-slate-400">{US_HOME_PRICING_SECTION.perVideo}</span>
                                </div>
                              </div>
                              <div className="flex-1 space-y-2 mb-6 border-t border-gray-100 pt-4 sm:pt-6">
                                <FeatureRow text={US_HOME_PRICING_SECTION.walkthroughFeature} />
                                <FeatureRow text={`${plan.videos} credits/month`} />
                                <FeatureRow text="24-hour delivery (Mon-Thu)" />
                                {viewWithDrone && <FeatureRow text="drone clips included" highlight={true} />}
                                <FeatureRow text="Additional credits available" isExtra={true} />
                                <FeatureRow text="Friday → delivered Monday" useInfoIcon />
                              </div>
                              <button
                                onClick={() => handleActivateSubscription(planId, viewWithDrone)}
                                disabled={isProcessing}
                                className={`btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                                  isPopular
                                    ? 'bg-[#3C59B2] text-white hover:bg-[#2d4a8f]'
                                    : 'bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200'
                                }`}
                              >
                                Get Started <ArrowRight className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </>
                    ) : isUsPartnersDashboard && !isFR ? (
                      <>
                        {usPartnerVolumePlans.map((plan) => (
                          <div
                            key={plan.id}
                            className={`card-hover bg-white rounded-3xl p-5 sm:p-6 md:p-8 border transition-all duration-300 relative flex flex-col ${
                              plan.popular ? 'border-[#3C59B2] shadow-xl z-10 md:-translate-y-4' : 'border-[#E2E8F0]'
                            }`}
                          >
                            {plan.savings ? (
                              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-700 px-4 sm:px-6 py-1.5 rounded-full text-[10px] sm:text-xs font-bold shadow-md whitespace-nowrap">
                                {plan.savings}
                              </div>
                            ) : null}
                            <div className="text-center mb-6">
                              <h3 className="font-manrope text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3">
                                {plan.properties} {US_PARTNERS_PRICING_SECTION.propertiesPerMonthUnit}
                              </h3>
                              <div className={`text-3xl sm:text-4xl font-bold tabular-nums ${plan.popular ? 'text-[#3C59B2]' : 'text-slate-900'}`}>
                                ${plan.price}
                                <span className="text-lg sm:text-xl font-semibold text-slate-500">{US_PARTNERS_PRICING_SECTION.perMonthShort}</span>
                              </div>
                              <div className="text-sm text-slate-500 mt-2">
                                ${plan.perProperty}{' '}
                                <span className="text-slate-400">{US_PARTNERS_PRICING_SECTION.perProperty}</span>
                              </div>
                            </div>
                            <div className="flex-1 space-y-2 mb-6 sm:mb-8 border-t border-gray-100 pt-4 sm:pt-6">
                              <FeatureRow text={US_PARTNERS_PRICING_SECTION.walkthroughFeature} />
                              <FeatureRow text={`${plan.properties} ${US_PARTNERS_PRICING_SECTION.creditsPerMonthSuffix}`} />
                              <FeatureRow text={US_PARTNERS_PRICING_SECTION.delivery} />
                              {viewWithDrone && <FeatureRow text={US_PARTNERS_PRICING_SECTION.droneIncluded} highlight={true} />}
                              <FeatureRow text={US_PARTNERS_PRICING_SECTION.buyExtra} isExtra={true} />
                              <FeatureRow text={US_PARTNERS_PRICING_SECTION.fridayOrders} useInfoIcon />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleActivateSubscription(plan.id, viewWithDrone)}
                              disabled={isProcessing}
                              className={`btn-premium overflow-hidden w-full py-3 sm:py-4 rounded-full font-bold text-center transition-all flex items-center justify-center gap-2 text-sm sm:text-base disabled:opacity-50 ${
                                plan.popular
                                  ? 'bg-[#3C59B2] text-white hover:bg-[#2d4a8f]'
                                  : 'bg-white text-[#3C59B2] border-2 border-[#3C59B2] hover:bg-slate-50'
                              }`}
                            >
                              Get Started <ArrowRight className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                    {/* STARTER */}
                    <div className="card-hover bg-white rounded-3xl p-6 border border-[#E2E8F0] relative flex flex-col">
                      <div className="text-center mb-4">
                        <h3 className={`font-manrope text-xl font-bold ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>{isFR ? '3 vidéos/mois' : '3 videos/month'}</h3>
                        <div className="mt-3 flex items-baseline justify-center">
                          <span className={`text-4xl font-extrabold ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>
                            {useEuros ? '€' : '$'}
                            {isFRAgentsPriceGrid
                              ? (viewWithDrone ? 570 : 450)
                              : (viewWithDrone ? PRICING.starter.withDrone : PRICING.starter.withoutDrone)}
                          </span>
                          <span className="text-gray-500 ml-1 font-medium">/{isFR ? 'mois' : 'mo'}</span>
                        </div>
                        <div className="mt-2 text-sm text-gray-500 font-medium">
                          {useEuros
                            ? `€${viewWithDrone ? (isFRAgentsPriceGrid ? 190 : PRICING.starter.withDrone / 3) : (isFRAgentsPriceGrid ? 150 : PRICING.starter.withoutDrone / 3)} par bien`
                            : `$${viewWithDrone ? PRICING.starter.withDrone / 3 : PRICING.starter.withoutDrone / 3}/property`}
                        </div>
                      </div>
                      <div className="flex-1 border-t border-gray-100 pt-4">
                        {!isFR && <FeatureRow text={"Walkthrough Video (Branded) with client's logo"} />}
                        <FeatureRow text={isFR ? '3 crédits/mois' : '3 credits/month'} />
                        <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                        {viewWithDrone && <FeatureRow text={isFR ? 'Séquences drone incluses' : 'drone clips included'} highlight={true} />}
                        <FeatureRow text={isFR ? 'Crédits à l\'unité disponibles' : 'Additional credits available'} isExtra={true} />
                        <FeatureRow text={isFR ? 'Vendredi → livré lundi' : 'Friday → delivered Monday'} useInfoIcon />
                      </div>
                      <button type="button" onClick={() => handleActivateSubscription('starter', viewWithDrone)} disabled={isProcessing} className={`btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${isFR ? 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50' : 'bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200'}`}>
                        {isFR ? 'Commencer' : 'Get Started'} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>

                    {/* PROFESSIONAL */}
                    <div className="card-hover bg-white rounded-3xl p-6 border-2 border-[#3C59B2] z-10 md:-translate-y-4 relative flex flex-col">
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-700 px-6 py-1.5 rounded-full text-xs font-bold shadow-md whitespace-nowrap">{viewWithDrone ? (isFR ? 'Économisez 5%' : 'Save 5%') : (isFR ? 'Économisez 7%' : 'Save 7%')}</div>
                      <div className="text-center mb-4">
                        <h3 className={`font-manrope text-xl font-bold ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>{isFR ? '6 vidéos/mois' : '6 videos/month'}</h3>
                        <div className="mt-3 flex items-baseline justify-center">
                          <span className="text-4xl font-extrabold text-[#3C59B2]">
                            {useEuros ? '€' : '$'}
                            {isFRAgentsPriceGrid
                              ? (viewWithDrone ? 1080 : 840)
                              : (viewWithDrone ? PRICING.professional.withDrone : PRICING.professional.withoutDrone)}
                          </span>
                          <span className="text-gray-500 ml-1 font-medium">/{isFR ? 'mois' : 'mo'}</span>
                        </div>
                        <div className="mt-2 text-sm text-gray-500 font-medium">
                          {useEuros
                            ? `€${viewWithDrone ? (isFRAgentsPriceGrid ? 180 : PRICING.professional.withDrone / 6) : (isFRAgentsPriceGrid ? 140 : PRICING.professional.withoutDrone / 6)} par bien`
                            : `$${viewWithDrone ? PRICING.professional.withDrone / 6 : PRICING.professional.withoutDrone / 6}/property`}
                        </div>
                      </div>
                      <div className="flex-1 border-t border-gray-100 pt-4">
                        {!isFR && <FeatureRow text={"Walkthrough Video (Branded) with client's logo"} />}
                        <FeatureRow text={isFR ? '6 crédits/mois' : '6 credits/month'} />
                        <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                        {viewWithDrone && <FeatureRow text={isFR ? 'Séquences drone incluses' : 'drone clips included'} highlight={true} />}
                        <FeatureRow text={isFR ? 'Crédits à l\'unité disponibles' : 'Additional credits available'} isExtra={true} />
                        <FeatureRow text={isFR ? 'Vendredi → livré lundi' : 'Friday → delivered Monday'} useInfoIcon />
                      </div>
                      <button type="button" onClick={() => handleActivateSubscription('professional', viewWithDrone)} disabled={isProcessing} className="btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center bg-[#3C59B2] text-white hover:bg-[#2d4a8f] transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                        {isFR ? 'Commencer' : 'Get Started'} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>

                    {/* ENTERPRISE */}
                    <div className="card-hover bg-white rounded-3xl p-6 border border-[#E2E8F0] relative flex flex-col">
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-700 px-6 py-1.5 rounded-full text-xs font-bold shadow-md whitespace-nowrap">{viewWithDrone ? (isFR ? 'Économisez 11%' : 'Save 11%') : (isFR ? 'Économisez 13%' : 'Save 13%')}</div>
                      <div className="text-center mb-4">
                        <h3 className={`font-manrope text-xl font-bold ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>{isFR ? '9 vidéos/mois' : '9 videos/month'}</h3>
                        <div className="mt-3 flex items-baseline justify-center">
                          <span className={`text-4xl font-extrabold ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>
                            {useEuros ? '€' : '$'}
                            {isFRAgentsPriceGrid
                              ? (viewWithDrone ? 1530 : 1170)
                              : (viewWithDrone ? PRICING.enterprise.withDrone : PRICING.enterprise.withoutDrone)}
                          </span>
                          <span className="text-gray-500 ml-1 font-medium">/{isFR ? 'mois' : 'mo'}</span>
                        </div>
                        <div className="mt-2 text-sm text-gray-500 font-medium">
                          {useEuros
                            ? `€${viewWithDrone ? (isFRAgentsPriceGrid ? 170 : PRICING.enterprise.withDrone / 9) : (isFRAgentsPriceGrid ? 130 : PRICING.enterprise.withoutDrone / 9)} par bien`
                            : `$${viewWithDrone ? PRICING.enterprise.withDrone / 9 : PRICING.enterprise.withoutDrone / 9}/property`}
                        </div>
                      </div>
                      <div className="flex-1 border-t border-gray-100 pt-4">
                        {!isFR && <FeatureRow text={"Walkthrough Video (Branded) with client's logo"} />}
                        <FeatureRow text={isFR ? '9 crédits/mois' : '9 credits/month'} />
                        <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                        {viewWithDrone && <FeatureRow text={isFR ? 'Séquences drone incluses' : 'drone clips included'} highlight={true} />}
                        <FeatureRow text={isFR ? 'Crédits à l\'unité disponibles' : 'Additional credits available'} isExtra={true} />
                        <FeatureRow text={isFR ? 'Vendredi → livré lundi' : 'Friday → delivered Monday'} useInfoIcon />
                      </div>
                      <button type="button" onClick={() => handleActivateSubscription('enterprise', viewWithDrone)} disabled={isProcessing} className={`btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${isFR ? 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50' : 'bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200'}`}>
                        {isFR ? 'Commencer' : 'Get Started'} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* TRY ONE PROPERTY */}
          {activeSection === 'try-one' && (
            <div>
              {(!canTryOne) && (
                <div className="mb-6 text-center bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-xl">
                  <p className="font-bold text-orange-800">{isFR ? 'Essai indisponible' : 'Try One not available'}</p>
                  <p className="text-sm text-orange-700 mt-1">{isFR ? 'Passez à un abonnement pour continuer.' : 'Please subscribe to continue.'}</p>
                </div>
              )}
              <div className="text-center mb-12">
                <h2 className={`font-manrope text-2xl md:text-3xl font-bold mb-3 animate-fade-in-up ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>{isFR ? 'Essayer une propriété' : 'Try One Property'}</h2>
                <p className="text-gray-600">{isFR ? 'Testez notre qualité avec une seule commande. Sans abonnement.' : 'Experience our quality with a single order. No subscription required.'}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <div className={`card-hover bg-white p-6 flex flex-col ${isFR ? 'rounded-3xl border border-[#E2E8F0]' : 'rounded-2xl border-2 border-gray-100 shadow-lg'}`}>
                  <div className="text-center mb-4">
                    <h3 className={`font-manrope text-xl font-bold ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>{isFR ? '1 crédit (Standard)' : '1 Credit (Standard)'}</h3>
                    <div className="mt-3 flex items-baseline justify-center">
                      <span className={`text-4xl font-extrabold ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>{useEuros ? '€' : '$'}{trialPriceStandard}</span>
                    </div>
                  </div>
                  <div className="flex-1 border-t border-gray-100 pt-4">
                    <p className="text-xs text-[#3C59B2] font-bold uppercase tracking-wide mb-3 text-center">{isFR ? 'Inclus :' : 'You get:'}</p>
                    {isFR ? <FeatureRow text="Vidéo Matterport" /> : <FeatureRow text={"Walkthrough Video (Branded) with client's logo"} />}
                    <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                    <FeatureRow text={isFR ? 'Vendredi → livré lundi' : 'Friday → delivered Monday'} useInfoIcon />
                  </div>
                  <button
                    onClick={() => handleTryOneProperty(false)}
                    disabled={isProcessing || !canTryOne}
                    className={`btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isFR ? 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50' : 'bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200'}`}
                  >
                    {isFR ? 'Acheter 1 crédit' : 'Buy 1 Credit'} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <div className={`card-hover bg-white p-6 flex flex-col ${isFR ? 'rounded-3xl border-2 border-[#3C59B2]' : 'rounded-3xl border-2 border-[#3C59B2]'}`}>
                  <div className="text-center mb-4">
                    <h3 className={`font-manrope text-xl font-bold ${isFR ? 'text-[#1e3a5f]' : 'text-gray-900'}`}>{isFR ? '1 crédit (avec drone)' : '1 Credit (with drone clips)'}</h3>
                    <div className="mt-3 flex items-baseline justify-center">
                      <span className="text-4xl font-extrabold text-[#3C59B2]">{useEuros ? '€' : '$'}{trialPriceDrone}</span>
                    </div>
                  </div>
                  <div className="flex-1 border-t border-gray-100 pt-4">
                    <p className="text-xs text-[#3C59B2] font-bold uppercase tracking-wide mb-3 text-center">{isFR ? 'Inclus :' : 'You get:'}</p>
                    {isFR ? <FeatureRow text="Vidéo Matterport" /> : <FeatureRow text={"Walkthrough Video (Branded) with client's logo"} />}
                    <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                    <FeatureRow text={isFR ? 'Séquence de drone' : 'Drone sequence'} highlight={true} />
                    <FeatureRow text={isFR ? 'Vendredi → livré lundi' : 'Friday → delivered Monday'} useInfoIcon />
                  </div>
                  <button
                    onClick={() => handleTryOneProperty(true)}
                    disabled={isProcessing || !canTryOne}
                    className="btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center bg-[#3C59B2] text-white hover:bg-[#2d4a8f] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isFR ? 'Acheter 1 crédit' : 'Buy 1 Credit'} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* BUY EXTRA CREDIT */}
          {activeSection === 'buy-credit' && isSubscriber && (
            <div>
              <div className="text-center mb-12">
                <h2 className="font-manrope text-2xl md:text-3xl font-bold text-gray-900 mb-3 animate-fade-in-up">
                  {isFR ? 'Acheter un crédit' : 'Buy Extra Credit'}
                </h2>
                <p className="text-gray-600">
                  {subscription.with_drone
                    ? (isFR
                      ? 'Besoin de plus ? Ajoutez un crédit avec ou sans drone pour cette propriété.'
                      : 'Need more? Add an extra credit with or without drone for this property.')
                    : (isFR
                      ? 'Besoin de plus ? Essayez aussi la version avec drone sur une propriété.'
                      : 'Need more? You can also try the drone version on a single property.')}
                </p>
              </div>
              <div className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                {subscription.with_drone ? (
                  <>
                    {/* Extra credit WITHOUT drone (to compare) */}
                    <div className="card-hover bg-white rounded-2xl p-6 border-2 border-gray-100 shadow-lg flex flex-col">
                      <div className="text-center mb-4">
                        <h3 className="font-manrope text-xl font-bold text-gray-900">
                          {isFR ? '1 crédit (sans drone)' : '1 Extra Credit (Standard)'}
                        </h3>
                        <div className="mt-3 flex items-baseline justify-center">
                          <span className="text-4xl font-extrabold text-gray-900">
                            {useEuros ? '€' : '$'}
                            {extraCreditPriceStandard}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 border-t border-gray-100 pt-4">
                        <p className="text-xs text-[#3C59B2] font-bold uppercase tracking-wide mb-3 text-center">
                          {isFR ? 'Inclus :' : 'You get:'}
                        </p>
                        <FeatureRow text={isFR ? 'Vidéo Matterport' : 'Matterport video'} />
                        <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          {isFR
                            ? 'Idéal pour comparer une vidéo sans drone sur une propriété.'
                            : 'Perfect to compare a non‑drone version on one property.'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleBuyCredit(false)}
                        disabled={isProcessing}
                        className="btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center bg-[#3C59B2] text-white hover:bg-[#2d4a8f] shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isFR ? 'Acheter ce crédit' : 'Purchase Credit'} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Extra credit WITH drone (same as plan) */}
                    <div className="card-hover bg-white rounded-3xl p-6 border-2 border-[#3C59B2] flex flex-col">
                      <div className="text-center mb-4">
                        <h3 className="font-manrope text-xl font-bold text-gray-900">
                          {isFR ? '1 crédit (avec drone)' : '1 Extra Credit (with drone)'}
                        </h3>
                        <div className="mt-3 flex items-baseline justify-center">
                          <span className="text-4xl font-extrabold text-[#3C59B2]">
                            {useEuros ? '€' : '$'}
                            {extraCreditPriceDrone}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 border-t border-gray-100 pt-4">
                        <p className="text-xs text-[#3C59B2] font-bold uppercase tracking-wide mb-3 text-center">
                          {isFR ? 'Inclus :' : 'You get:'}
                        </p>
                        <FeatureRow text={isFR ? 'Vidéo Matterport' : 'Matterport video'} />
                        <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                        <FeatureRow text={isFR ? 'Séquence de drone' : 'Drone sequence'} highlight={true} />
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          {isFR
                            ? 'Même configuration que votre abonnement actuel (avec drone).'
                            : 'Same setup as your current subscription (with drone).'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleBuyCredit(true)}
                        disabled={isProcessing}
                        className="btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center bg-[#3C59B2] text-white hover:bg-[#2d4a8f] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isFR ? 'Acheter ce crédit' : 'Purchase Credit'} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Extra credit STANDARD (same as plan) */}
                    <div className="card-hover bg-white rounded-2xl p-6 border-2 border-gray-100 shadow-lg flex flex-col">
                      <div className="text-center mb-4">
                        <h3 className="font-manrope text-xl font-bold text-gray-900">
                          {isFR ? '1 crédit (standard)' : '1 Extra Credit (Standard)'}
                        </h3>
                        <div className="mt-3 flex items-baseline justify-center">
                          <span className="text-4xl font-extrabold text-gray-900">
                            {useEuros ? '€' : '$'}
                            {extraCreditPriceStandard}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 border-t border-gray-100 pt-4">
                        <p className="text-xs text-[#3C59B2] font-bold uppercase tracking-wide mb-3 text-center">
                          {isFR ? 'Inclus :' : 'You get:'}
                        </p>
                        <FeatureRow text={isFR ? 'Vidéo Matterport' : 'Matterport video'} />
                        <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          {isFR
                            ? 'Identique à votre formule actuelle (sans drone).'
                            : 'Same as your current plan (without drone).'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleBuyCredit(false)}
                        disabled={isProcessing}
                        className="btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center bg-[#3C59B2] text-white hover:bg-[#2d4a8f] shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isFR ? 'Acheter ce crédit' : 'Purchase Credit'} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Extra credit WITH drone (to try the service) */}
                    <div className="card-hover bg-white rounded-3xl p-6 border-2 border-[#3C59B2] flex flex-col">
                      <div className="text-center mb-4">
                        <h3 className="font-manrope text-xl font-bold text-gray-900">
                          {isFR ? '1 crédit (avec drone)' : '1 Extra Credit (with drone)'}
                        </h3>
                        <div className="mt-3 flex items-baseline justify-center">
                          <span className="text-4xl font-extrabold text-[#3C59B2]">
                            {useEuros ? '€' : '$'}
                            {extraCreditPriceDrone}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 border-t border-gray-100 pt-4">
                        <p className="text-xs text-[#3C59B2] font-bold uppercase tracking-wide mb-3 text-center">
                          {isFR ? 'Inclus :' : 'You get:'}
                        </p>
                        <FeatureRow text={isFR ? 'Vidéo Matterport' : 'Matterport video'} />
                        <FeatureRow text={isFR ? 'Livraison 24 h (lun-jeu)' : '24-hour delivery (Mon-Thu)'} />
                        <FeatureRow text={isFR ? 'Séquence de drone' : 'Drone sequence'} highlight={true} />
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          {isFR
                            ? 'Testez les séquences drone sur une propriété sans changer d’abonnement.'
                            : 'Try drone clips on one property without changing your subscription.'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleBuyCredit(true)}
                        disabled={isProcessing}
                        className="btn-premium overflow-hidden w-full mt-4 py-3 rounded-full font-bold text-center bg-[#3C59B2] text-white hover:bg-[#2d4a8f] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isFR ? 'Acheter ce crédit' : 'Purchase Credit'} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
